/**
 * Safe localStorage wrapper with JSON serialization and error handling.
 */

/**
 * Get a value from localStorage, parsed from JSON.
 * Returns `defaultValue` if the key is missing or parsing fails.
 *
 * @param {string} key
 * @param {*} [defaultValue=null]
 * @returns {*}
 */
export function getItem(key, defaultValue = null) {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) return defaultValue
    return JSON.parse(raw)
  } catch {
    return defaultValue
  }
}

/**
 * Set a value in localStorage, serialized as JSON.
 * Returns true on success, false on error (e.g. quota exceeded).
 *
 * @param {string} key
 * @param {*} value
 * @returns {boolean}
 */
export function setItem(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch {
    return false
  }
}

/**
 * Remove a key from localStorage.
 *
 * @param {string} key
 */
export function removeItem(key) {
  try {
    localStorage.removeItem(key)
  } catch {
    // Silently ignore errors
  }
}

/**
 * Get a raw string value (no JSON parsing).
 * Useful for legacy keys that store plain strings.
 *
 * @param {string} key
 * @param {string} [defaultValue=null]
 * @returns {string|null}
 */
export function getString(key, defaultValue = null) {
  try {
    const raw = localStorage.getItem(key)
    return raw !== null ? raw : defaultValue
  } catch {
    return defaultValue
  }
}

/**
 * Set a raw string value (no JSON serialization).
 *
 * @param {string} key
 * @param {string} value
 * @returns {boolean}
 */
export function setString(key, value) {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    return false
  }
}
